import { useEffect, useState } from 'react'
import { SubformProps } from "."
import { uploadFile, fileToObj } from '../../services/fileUploadService'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { getIncomeDocErrorsMultiUpload, LoanApplication, SolarDoc } from '@oneethos/shared'
import BorrowerDocUpload from './borrower-doc-upload'

const normalizeDocStructure = (array) => {
  const result = array.reduce((acc, doc) => {
    const existingDocSet = acc.find(set => set.docType === doc.docType)
    if (existingDocSet) {
      existingDocSet.documents.push(doc)
      if (doc.docSubType != null) existingDocSet.docSubType = doc.docSubType
    } else {
      acc.push({ docType: doc.docType, documents: [doc], docSubType: doc.docSubType })
    }
    return acc
  }, [])
  return result
}

const uploadDoc = async (doc: File | SolarDoc, docType: string, docSubType?: string) => {
  if ('_id' in doc && doc._id) {
    const existing = doc as SolarDoc
    return docSubType !== undefined ? { ...existing, docSubType } : existing
  }

  const res = await uploadFile(doc as File)
  const saved = { ...fileToObj(doc as File), ...res, docType }
  return docSubType !== undefined ? { ...saved, docSubType } : saved
}

export const IncomeVerification = ({ formData, update, save, onError }: SubformProps) => {
  const [submitting, setSubmitting] = useState(false)
  const [showAddDocButton, setshowAddDocButton] = useState(false)
  const [showCoDocButton, setshowCoDocButton] = useState(false)
  const [docsArray, setDocsArray] = useState([])
  const [coDocsArray, setCoDocsArray] = useState([])
  const isJointApp = formData.isJointApp
  const primaryHasJointTaxReturn = docsArray.find(
    (d) => d.docType === 'taxreturn' && d.docSubType === 'joint'
  )

  useEffect(() => {
    if (formData.incomeVerificationDocuments?.length > 0) {
      setDocsArray(normalizeDocStructure(formData.incomeVerificationDocuments))
      setshowAddDocButton(true)
    }
    if (formData.coIncomeVerificationDocuments?.length > 0) {
      setCoDocsArray(normalizeDocStructure(formData.coIncomeVerificationDocuments))
      setshowCoDocButton(true)
    }
  }, [])

  const hasDocs = docsArray.some(docSet => docSet.documents?.length)
  const hasCoDocs = coDocsArray.some(docSet => docSet.documents?.length)
  const displayContinueBtn = formData.isJointApp && !primaryHasJointTaxReturn ? (hasCoDocs && hasDocs) : hasDocs

  const errors = getIncomeDocErrorsMultiUpload(
    formData as LoanApplication, 
    docsArray, 
    primaryHasJointTaxReturn ? [] : coDocsArray
  )

  const handleAddBorrowerDocUpload = (isCoBorrower = false) => {
    const newDoc = { docType: '', documents: [] }
    if (isCoBorrower) {
      setCoDocsArray([...coDocsArray, newDoc])
      update({
        ...formData,
        coIncomeVerificationDocuments: [...coDocsArray, newDoc]
      })
    } else {
      setDocsArray([...docsArray, newDoc])
      update({
        ...formData,
        incomeVerificationDocuments: [...docsArray, newDoc]
      })
    }
  }

  const handleDocTypeChange = (ev, index, isCoBorrower, isFirstDocument = false) => {
    const borrowerDocsArray = isCoBorrower ? coDocsArray : docsArray
    const updateBorrowerDocsState = isCoBorrower ? setCoDocsArray : setDocsArray
    const formDataKey = isCoBorrower ? 'coIncomeVerificationDocuments' : 'incomeVerificationDocuments'

    const targetIndex = isFirstDocument ? 0 : index + 1
    const updatedDoc = { docType: ev.target.value, documents: [], docSubType: undefined }

    let updatedArray: typeof borrowerDocsArray
    
    if (isFirstDocument && borrowerDocsArray.length === 0) {
      updatedArray = [updatedDoc]
    } else {
      updatedArray = borrowerDocsArray.map((d, i) => {
        return i === targetIndex ? updatedDoc : d
      })
    }

    updateBorrowerDocsState(updatedArray)
    update({
      ...formData,
      [formDataKey]: updatedArray
    })
  }

  const handleDocSubTypeChange = (ev, index, isCoBorrower) => {
    const borrowerDocsArray = isCoBorrower ? coDocsArray : docsArray
    const updateBorrowerDocsState = isCoBorrower ? setCoDocsArray : setDocsArray
    const formDataKey = isCoBorrower ? 'coIncomeVerificationDocuments' : 'incomeVerificationDocuments'
    const docSubType = ev.target.value || undefined

    const updated = borrowerDocsArray.map((d, i) => i === index ? { ...d, docSubType, documents: [] } : d)
    updateBorrowerDocsState(updated)
    update({ ...formData, [formDataKey]: updated })
  }

  const handleDocUpload = (isCoBorrower, index, docs) => {
    const updatedDocs = isCoBorrower ? coDocsArray : docsArray
    const updatedDocSet = updatedDocs.map((d, i) => {
      return i === index ? { ...d, documents: docs } : d
    })

    if (isCoBorrower) {
      setCoDocsArray(updatedDocSet)
      setshowCoDocButton(updatedDocSet.some(docSet => docSet.documents.length > 0))
      update({ ...formData, coIncomeVerificationDocuments: updatedDocSet })
    } else {
      setDocsArray(updatedDocSet)
      setshowAddDocButton(updatedDocSet.some(docSet => docSet.documents.length > 0))
      update({ ...formData, incomeVerificationDocuments: updatedDocSet })
    }
  }

  const handleContinue = async () => {
    try {
      setSubmitting(true)
      const bDocProm = Promise.all(
        docsArray.flatMap(docSet =>
          docSet.documents.map((doc) => {
            const docsSubType = (docSet.docType === 'paystubs' || docSet.docType === 'taxreturn') ? docSet.docSubType : undefined
            return uploadDoc(doc, docSet.docType, docsSubType)
          }))
      )

      const coDocProm = Promise.all(
        coDocsArray.flatMap(docSet =>
          docSet.documents.map((doc) => {
            const docsSubType = (docSet.docType === 'paystubs' || docSet.docType === 'taxreturn') ? docSet.docSubType : undefined
            return uploadDoc(doc, docSet.docType, docsSubType)
          }))
      )

      const [borrowerDocs, coBorrowerDocs] = await Promise.all([bDocProm, coDocProm])
      await save({
        data: {
          ...formData,
          incomeVerificationDocuments: borrowerDocs,
          coIncomeVerificationDocuments: coBorrowerDocs
        }
      })
    } catch (ex) {
      onError(ex.message || ex.error)
      setSubmitting(false)
    }
  }

  if (submitting) {
    return (
      <div style={{ padding: '3em', display: 'flex', flexFlow: 'column nowrap', alignItems: 'center' }}>
        <Spinner style={{ marginBottom: '2em' }} size={SpinnerSize.large} />
        <div style={{ fontSize: '1.5em', textAlign: 'center' }}>
          Uploading documents and saving your progress...
        </div>
      </div>
    )
  }

  return (
    <>
      <BorrowerDocUpload
        title="Income Verification - Primary Borrower"
        incomeDocuments={docsArray}
        onDocTypeChange={handleDocTypeChange}
        onDocSubTypeChange={handleDocSubTypeChange}
        onChangeDoc={handleDocUpload}
        isCoBorrower={false}
        isJointApp={isJointApp}
      />
      {showAddDocButton && (
        <button type="button" onClick={() => handleAddBorrowerDocUpload(false)} className="btn btn-secondary mb-3">
          Add another document
        </button>
      )}
      {formData.isJointApp && !primaryHasJointTaxReturn && (
        <>
          <BorrowerDocUpload
            title="Income Verification - Co-Borrower"
            incomeDocuments={coDocsArray}
            onDocTypeChange={handleDocTypeChange}
            onDocSubTypeChange={handleDocSubTypeChange}
            onChangeDoc={handleDocUpload}
            isCoBorrower={true}
            isJointApp={isJointApp}
          />
          {showCoDocButton && (
            <button type="button" onClick={() => handleAddBorrowerDocUpload(true)} className="btn btn-secondary mb-3">
              Add another document
            </button>
          )}
        </>
      )}
      {(displayContinueBtn) &&
        (
          errors.length ? <div className="alert alert-info">{errors.map((err, i) => <div key={i}>{err}</div>)}</div> :
            <button
              type="button"
              className="btn btn-primary mb-3 ms-3"
              onClick={handleContinue}
            >
              Continue
            </button>
        )}
    </>
  )
}

export default IncomeVerification
